/**
 * Country name → flag emoji for player cards. The trivia data stores nationality as a country *name*
 * (Transfermarkt style), so we map the common football nations to ISO-3166 alpha-2 and convert to the
 * regional-indicator emoji. UK home nations use their special tag-sequence flags. Unmapped → '' (the
 * card simply renders no flag — a graceful miss, never an error).
 */

/** ISO alpha-2 → flag emoji via regional indicator symbols. */
function isoToEmoji(iso2: string): string {
  return iso2
    .toUpperCase()
    .replace(/./g, (ch) => String.fromCodePoint(127397 + ch.charCodeAt(0)));
}

/** Home nations + anything that isn't a plain ISO country. */
const SPECIAL: Record<string, string> = {
  England: '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
  Scotland: '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
  Wales: '🏴󠁧󠁢󠁷󠁬󠁳󠁿',
  'Northern Ireland': '🇬🇧',
};

/** Football-relevant country name → ISO alpha-2. Extend as gaps surface; misses degrade to no flag. */
const NAME_TO_ISO: Record<string, string> = {
  Argentina: 'AR', Brazil: 'BR', France: 'FR', Germany: 'DE', Spain: 'ES', Portugal: 'PT',
  Italy: 'IT', Netherlands: 'NL', Belgium: 'BE', Croatia: 'HR', Uruguay: 'UY', Colombia: 'CO',
  Mexico: 'MX', 'United States': 'US', USA: 'US', Poland: 'PL', Norway: 'NO', Sweden: 'SE',
  Denmark: 'DK', Switzerland: 'CH', Austria: 'AT', Serbia: 'RS', Senegal: 'SN', Nigeria: 'NG',
  Ghana: 'GH', Cameroon: 'CM', "Cote d'Ivoire": 'CI', 'Ivory Coast': 'CI', Egypt: 'EG',
  Morocco: 'MA', Algeria: 'DZ', Tunisia: 'TN', Japan: 'JP', 'South Korea': 'KR', 'Korea, South': 'KR',
  Australia: 'AU', Canada: 'CA', Chile: 'CL', Peru: 'PE', Ecuador: 'EC', Paraguay: 'PY',
  Venezuela: 'VE', Turkey: 'TR', Türkiye: 'TR', Greece: 'GR', Russia: 'RU', Ukraine: 'UA',
  'Czech Republic': 'CZ', Czechia: 'CZ', Slovakia: 'SK', Slovenia: 'SI', Hungary: 'HU',
  Romania: 'RO', Bulgaria: 'BG', Ireland: 'IE', 'Republic of Ireland': 'IE', Iceland: 'IS',
  Finland: 'FI', Georgia: 'GE', Armenia: 'AM', Albania: 'AL', 'North Macedonia': 'MK',
  'Bosnia-Herzegovina': 'BA', 'Bosnia and Herzegovina': 'BA', Montenegro: 'ME', Kosovo: 'XK',
  Israel: 'IL', 'Saudi Arabia': 'SA', Iran: 'IR', Iraq: 'IQ', Qatar: 'QA', 'United Arab Emirates': 'AE',
  China: 'CN', Jamaica: 'JM', 'Costa Rica': 'CR', Panama: 'PA', Honduras: 'HN', Mali: 'ML',
  Guinea: 'GN', 'Burkina Faso': 'BF', 'DR Congo': 'CD', 'Congo DR': 'CD', Gabon: 'GA',
  'South Africa': 'ZA', Angola: 'AO', 'Cape Verde': 'CV', Zambia: 'ZM', Uzbekistan: 'UZ',
};

/** @returns flag emoji for a country name, or '' if unknown. */
export function countryFlag(name: string | null | undefined): string {
  if (!name) return '';
  const trimmed = name.trim();
  if (SPECIAL[trimmed]) return SPECIAL[trimmed];
  const iso = NAME_TO_ISO[trimmed];
  return iso ? isoToEmoji(iso) : '';
}

/** Home nations → flagcdn subdivision codes (NI falls back to the UK flag). */
const NAME_TO_FLAGCDN_CODE: Record<string, string> = {
  England: 'gb-eng',
  Scotland: 'gb-sct',
  Wales: 'gb-wls',
  'Northern Ireland': 'gb',
};

/**
 * @returns a flagcdn-ready country code ('fr', 'gb-eng') for a country name, or null if unknown —
 * lets callers render a real flag IMAGE (`<CountryFlag>`) instead of an emoji that Android can't draw.
 */
export function countryCodeFromName(name: string | null | undefined): string | null {
  if (!name) return null;
  const trimmed = name.trim();
  if (NAME_TO_FLAGCDN_CODE[trimmed]) return NAME_TO_FLAGCDN_CODE[trimmed];
  const iso = NAME_TO_ISO[trimmed];
  return iso ? iso.toLowerCase() : null;
}
